import type {
  Position,
  Area,
  VesselItinerary,
  VesselRestriction,
  VesselVetting,
  VesselObservation,
} from '@/types/positionList.types';
import { ApiClient } from '@/config/axios.config';

/**
 * PositionListService - handles all API calls related to position list
 */
export class PositionListService {
  /**
   * Get all available areas
   */
  static async getAreas(): Promise<string[]> {
    try {
      const response = await ApiClient.get<Area[]>('/positionlist/areas');
      return response.map((area) => area.area);
    } catch (error) {
      console.error('Error fetching areas:', error);
      throw new Error('Failed to fetch areas');
    }
  }

  /**
   * Get position list for a specific area
   */
  static async getPositionList(area: string): Promise<Position[]> {
    try {
      const response = await ApiClient.get<Position[]>(`/positionlists?area=${area}`);
      return response;
    } catch (error) {
      console.error(`Error fetching position list for ${area}:`, error);
      throw new Error(`Failed to fetch position list for ${area}`);
    }
  }

  /**
   * Refresh position list for a specific area (force fetch from server)
   */
  static async refreshPositionList(area: string): Promise<Position[]> {
    try {
      const response = await ApiClient.get<Position[]>(
        `/positionlists?area=${area}&_=${Date.now()}`
      );
      return response;
    } catch (error) {
      console.error(`Error refreshing position list for ${area}:`, error);
      throw new Error(`Failed to refresh position list for ${area}`);
    }
  }

  /**
   * Get a single position by ID (if needed)
   */
  static async getPositionById(id: number, area: string): Promise<Position | undefined> {
    try {
      const positions = await this.getPositionList(area);
      return positions.find((p) => p.id === id);
    } catch (error) {
      console.error(`Error fetching position ${id}:`, error);
      throw new Error(`Failed to fetch position ${id}`);
    }
  }

  /**
   * Get detailed position information
   */
  static async getPositionDetails(area: string, id: number): Promise<Position> {
    try {
      const response = await ApiClient.get<Position>(`/positionlists?area=${area}&id=${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching position details for ${id}:`, error);
      throw new Error(`Failed to fetch position details for ${id}`);
    }
  }

  /**
   * Get vessel itineraries
   */
  static async getVesselItineraries(vesselId: number): Promise<VesselItinerary[]> {
    try {
      const response = await ApiClient.get<VesselItinerary[]>(
        `/positionlist/vesselitineraries/${vesselId}`
      );
      return response;
    } catch (error) {
      console.error(`Error fetching vessel itineraries for ${vesselId}:`, error);
      throw new Error(`Failed to fetch vessel itineraries for ${vesselId}`);
    }
  }

  /**
   * Get vessel restrictions
   */
  static async getVesselRestrictions(vesselId: number): Promise<VesselRestriction[]> {
    try {
      const response = await ApiClient.get<VesselRestriction[]>(
        `/positionlist/vesselrestrictions/${vesselId}`
      );
      return response;
    } catch (error) {
      console.error(`Error fetching vessel restrictions for ${vesselId}:`, error);
      throw new Error(`Failed to fetch vessel restrictions for ${vesselId}`);
    }
  }

  /**
   * Get vessel vettings
   */
  static async getVesselVettings(vesselId: number): Promise<VesselVetting[]> {
    try {
      const response = await ApiClient.get<VesselVetting[]>(
        `/positionlist/vesselvettings/${vesselId}`
      );
      return response;
    } catch (error) {
      console.error(`Error fetching vessel vettings for ${vesselId}:`, error);
      throw new Error(`Failed to fetch vessel vettings for ${vesselId}`);
    }
  }

  /**
   * Get vessel observations
   */
  static async getVesselObservations(vesselId: number): Promise<VesselObservation[]> {
    try {
      const response = await ApiClient.get<VesselObservation[]>(
        `/positionlist/vesselobservations/${vesselId}`
      );
      return response;
    } catch (error) {
      console.error(`Error fetching vessel observations for ${vesselId}:`, error);
      throw new Error(`Failed to fetch vessel observations for ${vesselId}`);
    }
  }

  /**
   * Get position statistics for an area
   */
  static getPositionStats(positions: Position[]): {
    total: number;
    onSubs: number;
    withScrubber: number;
    withRestrictions: number;
    vlcc: number;
    suezmax: number;
    aframax: number;
  } {
    return {
      total: positions.length,
      onSubs: positions.filter((p) => p.bitOnSubs === 1).length,
      withScrubber: positions.filter((p) => p.hasScrubber === 'Yes').length,
      withRestrictions: positions.filter((p) => p.onRestrictionList === 1).length,
      vlcc: positions.filter((p) => p.vesselSize === 2).length,
      suezmax: positions.filter((p) => p.vesselSize === 1).length,
      aframax: positions.filter((p) => p.vesselSize === 0).length,
    };
  }
}
